/**
 * Upload Portal Page
 * TASK-361: Document Management & Compliance System
 *
 * Public page for clients to upload documents through a secure link.
 * Requires OTP verification before allowing uploads.
 *
 * Steps:
 * 1. Loading - Validate token
 * 2. Info - Show portal details, masked email
 * 3. OTP - Request and verify code
 * 4. Upload - File selection and upload
 * 5. Success - Confirmation message
 * 6. Error - Clear error states
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Shield,
  Mail,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
  FileText,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import { publicUploadService } from '../../services/publicUploadService';
import {
  PortalInfo,
  DocType,
  getDocTypeLabel,
} from '../../types/documentCompliance';
import OTPInput from '../../components/public/OTPInput';
import FileUploadZone from '../../components/public/FileUploadZone';
import PortalLayout from '../../components/public/PortalLayout';

type PortalStep = 'loading' | 'info' | 'otp' | 'upload' | 'success' | 'error';

const UploadPortalPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  // State
  const [step, setStep] = useState<PortalStep>('loading');
  const [portalInfo, setPortalInfo] = useState<PortalInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // OTP state
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);

  // Session state
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [uploadsRemaining, setUploadsRemaining] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);

  // Fetch portal info on mount
  useEffect(() => {
    if (token) {
      fetchPortalInfo();
    } else {
      setStep('error');
      setError('Invalid upload link');
    }
  }, [token]);

  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (otpCountdown === 0 && step === 'otp') {
      setCanResendOtp(true);
    }
  }, [otpCountdown, step]);

  const fetchPortalInfo = async () => {
    try {
      setIsLoading(true);
      const info = await publicUploadService.getPortalInfo(token!);
      setPortalInfo(info);

      if (info.is_expired) {
        setStep('error');
        setError('This upload link has expired');
      } else if (info.is_completed) {
        setStep('error');
        setError('This upload link has already been used to its limit');
      } else {
        setUploadsRemaining(info.uploads_remaining);
        setStep('info');
      }
    } catch (err) {
      console.error('Failed to fetch portal info:', err);
      setStep('error');
      setError('This upload link is invalid or has expired');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    try {
      setIsLoading(true);
      setOtpError(null);
      await publicUploadService.requestOTP(token!);
      setStep('otp');
      setOtpCountdown(600); // 10 minutes
      setCanResendOtp(false);
    } catch (err: unknown) {
      console.error('Failed to request OTP:', err);
      setOtpError(
        axios.isAxiosError(err)
          ? err.response?.data?.detail || 'Failed to send verification code. Please try again.'
          : 'Failed to send verification code. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      setOtpError('Please enter a 6-digit code');
      return;
    }

    try {
      setIsLoading(true);
      setOtpError(null);
      const response = await publicUploadService.verifyOTP(token!, otpCode);

      if (response.success && response.session_token) {
        setSessionToken(response.session_token);
        setUploadsRemaining(response.uploads_remaining);
        setStep('upload');
      } else {
        setOtpError(response.message || 'Invalid verification code');
      }
    } catch (err: unknown) {
      console.error('Failed to verify OTP:', err);
      setOtpError(
        axios.isAxiosError(err)
          ? err.response?.data?.detail || 'Invalid or expired verification code'
          : 'Invalid or expired verification code'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadFile = async (file: File, docType: DocType) => {
    if (!sessionToken) throw new Error('Session expired');

    try {
      const response = await publicUploadService.uploadFile(
        token!,
        sessionToken,
        file,
        docType
      );

      if (response.success) {
        setUploadsRemaining(response.uploads_remaining);
        setUploadedCount((c) => c + 1);

        // If no more uploads remaining, show success
        if (response.uploads_remaining === 0) {
          setStep('success');
        }
      }
    } catch (err) {
      console.error('Failed to upload file:', err);
      throw err;
    }
  };

  const handleFinish = () => {
    setStep('success');
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading step
  if (step === 'loading') {
    return (
      <PortalLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 text-[#002B45] animate-spin mb-4" />
          <p className="text-gray-600">Loading upload portal...</p>
        </div>
      </PortalLayout>
    );
  }

  // Error step
  if (step === 'error') {
    return (
      <PortalLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Unable to Access Portal
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            Please contact your relationship manager for a new upload link.
          </p>
        </div>
      </PortalLayout>
    );
  }

  // Success step
  if (step === 'success') {
    return (
      <PortalLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Documents Uploaded Successfully
          </h2>
          <p className="text-gray-600 mb-6">
            Thank you! Your {uploadedCount} document{uploadedCount !== 1 ? 's have' : ' has'} been
            submitted for review. Your relationship manager will be notified.
          </p>
          <p className="text-sm text-gray-500">
            You can close this window now.
          </p>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      {/* Info Step */}
      {step === 'info' && portalInfo && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#002B45]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-8 h-8 text-[#002B45]" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Document Upload Request
            </h2>
            <p className="text-gray-600">
              {portalInfo.company_name} has requested documents from you
            </p>
          </div>

          {/* Portal Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            {/* Masked Email */}
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Your email</p>
                <p className="font-medium text-gray-900">{portalInfo.client_email_masked}</p>
              </div>
            </div>

            {/* Document Types */}
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Requested documents</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(portalInfo.document_types ?? []).map((type) => (
                    <span
                      key={type}
                      className="px-2 py-0.5 text-xs bg-white border border-gray-200 rounded"
                    >
                      {getDocTypeLabel(type)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Uploads Remaining */}
            <div className="flex items-center gap-3">
              <Upload className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Upload limit</p>
                <p className="font-medium text-gray-900">
                  {portalInfo.uploads_remaining} of {portalInfo.max_uploads} remaining
                </p>
              </div>
            </div>

            {/* Expiration */}
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Link expires</p>
                <p className="font-medium text-gray-900">
                  {new Date(portalInfo.expires_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Message from RM */}
          {portalInfo.message_to_client && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-900 mb-1">Message from your advisor:</p>
              <p className="text-sm text-blue-800">{portalInfo.message_to_client}</p>
            </div>
          )}

          {/* Error Display */}
          {otpError && (
            <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg">
              {otpError}
            </div>
          )}

          {/* Continue Button */}
          <button
            onClick={handleRequestOTP}
            disabled={isLoading}
            className="
              w-full py-3 px-4 bg-[#002B45] text-white font-medium rounded-lg
              hover:bg-[#003a5c] disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2 transition-colors
            "
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending verification code...
              </>
            ) : (
              <>
                <Shield className="w-5 h-5" />
                Verify Identity to Continue
              </>
            )}
          </button>

          <p className="text-xs text-center text-gray-500">
            A verification code will be sent to your email address
          </p>
        </div>
      )}

      {/* OTP Step */}
      {step === 'otp' && portalInfo && (
        <div className="space-y-6">
          <button
            onClick={() => setStep('info')}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="text-center">
            <div className="w-16 h-16 bg-[#002B45]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#002B45]" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Enter Verification Code
            </h2>
            <p className="text-gray-600">
              We've sent a 6-digit code to {portalInfo.client_email_masked}
            </p>
          </div>

          {/* OTP Input */}
          <div className="py-4">
            <OTPInput
              value={otpCode}
              onChange={setOtpCode}
              error={otpError || undefined}
              disabled={isLoading}
            />
          </div>

          {/* Countdown / Resend */}
          <div className="text-center">
            {otpCountdown > 0 ? (
              <p className="text-sm text-gray-500">
                Code expires in <span className="font-medium">{formatCountdown(otpCountdown)}</span>
              </p>
            ) : canResendOtp ? (
              <button
                onClick={handleRequestOTP}
                disabled={isLoading}
                className="text-sm text-[#002B45] hover:underline flex items-center gap-1 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Resend code
              </button>
            ) : null}
          </div>

          {/* Verify Button */}
          <button
            onClick={handleVerifyOTP}
            disabled={isLoading || otpCode.length !== 6}
            className="
              w-full py-3 px-4 bg-[#002B45] text-white font-medium rounded-lg
              hover:bg-[#003a5c] disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2 transition-colors
            "
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify & Continue'
            )}
          </button>
        </div>
      )}

      {/* Upload Step */}
      {step === 'upload' && portalInfo && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Identity Verified
            </h2>
            <p className="text-gray-600">
              You can now upload your documents
            </p>
          </div>

          {/* Upload Zone */}
          <FileUploadZone
            allowedDocTypes={portalInfo.document_types ?? []}
            onUpload={handleUploadFile}
            uploadsRemaining={uploadsRemaining}
            disabled={uploadsRemaining === 0}
          />

          {/* Finish Button */}
          {uploadedCount > 0 && (
            <button
              onClick={handleFinish}
              className="
                w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg
                hover:bg-green-700 flex items-center justify-center gap-2 transition-colors
              "
            >
              <CheckCircle className="w-5 h-5" />
              Finish ({uploadedCount} uploaded)
            </button>
          )}

          <p className="text-xs text-center text-gray-500">
            {uploadsRemaining} upload{uploadsRemaining !== 1 ? 's' : ''} remaining
          </p>
        </div>
      )}
    </PortalLayout>
  );
};

export default UploadPortalPage;
